import { StatusCodes } from "http-status-codes";
import { ErrorService } from "../domain/services/ErrorService";
import { BehaviorSubject, Subscription } from "rxjs";
import { ErrorResponseSubject } from "../types/ErrorResponseSubject";
import { apiURL } from "../Config";
import { userServiceFactory } from "./UserServiceImpl";

export class ErrorServiceImpl implements ErrorService {
    private apiUrl: URL;
    private constructor() {
        this.apiUrl = new URL(apiURL);
    }
    private static _instance: ErrorService;
    private static _errorResponseSubject: ErrorResponseSubject =
        ErrorServiceImpl.getInitialErrorResponseSubject();

    private static getInitialErrorResponseSubject(): ErrorResponseSubject {
        return {
            [StatusCodes.UNAUTHORIZED]: new BehaviorSubject(false),
            [StatusCodes.CONFLICT]: new BehaviorSubject(false),
            [StatusCodes.BAD_REQUEST]: new BehaviorSubject(false),
            [StatusCodes.FORBIDDEN]: new BehaviorSubject(false),
            [StatusCodes.NOT_FOUND]: new BehaviorSubject(false),
            [StatusCodes.REQUEST_TOO_LONG]: new BehaviorSubject(false),
        };
    }

    static getInstance() {
        if (!ErrorServiceImpl._instance) {
            ErrorServiceImpl._instance = new ErrorServiceImpl();
        }
        return ErrorServiceImpl._instance;
    }

    private clearToken() {
        userServiceFactory().clearToken();
    }

    async throwResponseError(response: any) {
        const responseJSON = await response.json();
        throw new Error(
            JSON.stringify({
                statusText: response.statusText,
                data: responseJSON,
            })
        );
    }

    subscribeOnError(
        statusCode: StatusCodes,
        onErrorCallback?: () => void
    ): Subscription {
        return ErrorServiceImpl._errorResponseSubject[statusCode].subscribe(
            (error) => {
                if (error && onErrorCallback) {
                    onErrorCallback();
                }
            }
        );
    }

    getStatusFromErrorResponseSubject(statusCode: StatusCodes): boolean {
        return ErrorServiceImpl._errorResponseSubject[statusCode].getValue();
    }

    notifyErrorResponseSubjectListeners(
        statusCode: StatusCodes,
        value: boolean
    ): void {
        ErrorServiceImpl._errorResponseSubject[statusCode].next(value);
    }

    /**
     * Checks if the provided URL belongs to the project's API.
     * Prevents logout loops when receiving 401 from third-party services like Google.
     */
    isProjectApiURL(url: string): boolean {
        try {
            const inputUrl = new URL(url);
            return inputUrl.hostname === this.apiUrl.hostname;
        } catch {
            return false;
        }
    }

    async handleResponseError(response: Response): Promise<void> {
        switch (response.status) {
            case StatusCodes.UNAUTHORIZED:
                if (this.isProjectApiURL(response.url)) {
                    this.clearToken();
                }
                this.notifyErrorResponseSubjectListeners(
                    StatusCodes.UNAUTHORIZED,
                    true
                );
                break;
            case StatusCodes.CONFLICT:
                this.notifyErrorResponseSubjectListeners(
                    StatusCodes.CONFLICT,
                    true
                );
                break;
            case StatusCodes.BAD_REQUEST:
                this.notifyErrorResponseSubjectListeners(
                    StatusCodes.BAD_REQUEST,
                    true
                );
                break;
            case StatusCodes.FORBIDDEN:
                this.notifyErrorResponseSubjectListeners(
                    StatusCodes.FORBIDDEN,
                    true
                );
                break;
            case StatusCodes.NOT_FOUND:
                this.notifyErrorResponseSubjectListeners(
                    StatusCodes.NOT_FOUND,
                    true
                );
                break;
            case StatusCodes.REQUEST_TOO_LONG:
                this.notifyErrorResponseSubjectListeners(
                    StatusCodes.REQUEST_TOO_LONG,
                    true
                );
                break;
            case StatusCodes.OK:
                break;
            default:
                await this.throwResponseError(response);
                break;
        }
    }

    resetErrorResponseSubject(): void {
        Object.values(ErrorServiceImpl._errorResponseSubject).forEach((obj) => {
            obj.next(false);
        });
    }
}

export function errorServiceFactory(): ErrorService {
    return ErrorServiceImpl.getInstance();
}
